import { Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { SelectChangeEvent } from "@mui/material/Select";
import React from "react";
import { Controller } from "react-hook-form";

import IconRightArrow from "../../../../assets/images/icons/icon_right_arrow.svg";
import HorizontalBox from "../../../../components/Boxes/HorizontalBox/HorizontalBox";
import Button from "../../../../components/Buttons/Button/Button";
import ImgIcon from "../../../../components/ImgIcon/ImgIcon";
import { CloseIcon } from "../../../../components/Modal/NextStepDialog/NextStepDialog.styles";
import SingleSelect from "../../../../components/SingleSelect/SingleSelect";
import useDocumentsContext from "../../hooks/useDocumentsContext.hooks";
import { InfoTooltip } from "../infoTooltip/InfoTooltip";
import { EditPanelTooltipText } from "./EditPanel.constants";
import { PanelActionBox, Title } from "./EditPanel.styles";
import useEditPanel from "./useEditPanel.hooks";

type File = { id: string; type: "folder" | "file"; fileType?: string };

interface Props {
  title: string;
  open?: boolean;
  handleClose?: () => void;
  files: File[];
  reload: Function;
}

const EditPanel = ({
  title,
  open = false,
  handleClose = () => {},
  files = [],
  reload,
}: Props) => {
  const { state, updateFiles } = useDocumentsContext();

  const {
    docControlOptions,
    handleSubmit,
    handleSave,
    onClose,
    loading,
    control,
    pdfFileCount,
  } = useEditPanel(state, { handleClose, files, updateFiles, reload });

  return (
    <React.Fragment key="right">
      <HorizontalBox>
        <Title id="title">
          {title} <ImgIcon icon={IconRightArrow} />
        </Title>
        <PanelActionBox>
          <CloseIcon
            id="btn_bulk_edit_file_close"
            onClick={onClose}
            color="primary"
          />
        </PanelActionBox>
      </HorizontalBox>
      <Grid
        container
        sx={{ padding: (theme) => theme.spacing(2.5, 4.5) }}
        rowSpacing={2}
      >
        <Grid item xs={12}>
          <Typography>
            <strong> PDF Specific Settings </strong>
          </Typography>

          {pdfFileCount ? (
            <Typography>
              <small>
                {" "}
                {`${pdfFileCount} PDF ${
                  pdfFileCount > 1 ? "files" : "file"
                } selected`}{" "}
              </small>
            </Typography>
          ) : (
            <Typography color={"error"}>
              <small> Please select a PDF file first. </small>
            </Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography>
            {/* <small> */}
            Any adjustments made in 'PDF Specific Settings' will only affect PDF
            files in this bulk edit. Please be aware that settings applied to
            PDFs before upload will persist.
            {/* </small> */}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{ padding: (theme) => theme.spacing(2.5, 4.5) }}
        rowSpacing={2}
      >
        <Grid item xs={12}>
          <InfoTooltip
            id="edit_panel_download_tooltip"
            label="Download"
            children={EditPanelTooltipText.download}
          />
        </Grid>

        <Grid container xs={12} justifyContent={"space-between"}>
          <Grid item xs={12}>
            <Controller
              name="downloadEnabled"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                return (
                  <SingleSelect
                    id="select_downloadEnabled"
                    disabled={loading || !pdfFileCount}
                    optionList={docControlOptions}
                    label=""
                    fullWidth={true}
                    idField="id"
                    labelField="label"
                    handleOptionSelection={(event: SelectChangeEvent) =>
                      onChange(event.target.value)
                    }
                    defaultValue={"keepExisting"}
                    value={value ?? ""}
                    error={!!error}
                    noGutter={true}
                  />
                );
              }}
              control={control}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <InfoTooltip
              id="edit_panel_print_tooltip"
              label="Print"
              children={EditPanelTooltipText.print}
            />
          </Grid>
        </Grid>
        <Grid container xs={12} justifyContent={"space-between"}>
          <Grid item xs={12}>
            <Controller
              name="printEnabled"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                return (
                  <SingleSelect
                    id="select_printEnabled"
                    disabled={loading || !pdfFileCount}
                    optionList={docControlOptions}
                    label=""
                    fullWidth={true}
                    idField="id"
                    labelField="label"
                    handleOptionSelection={(event: SelectChangeEvent) =>
                      onChange(event.target.value)
                    }
                    defaultValue={"keepExisting"}
                    value={value ?? ""}
                    error={!!error}
                    noGutter={true}
                    helperText={error?.message ?? ""}
                  />
                );
              }}
              control={control}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <InfoTooltip
            id="edit_panel_print_tooltip"
            label="Copy"
            children={EditPanelTooltipText.copy}
          />
        </Grid>
        <Grid container xs={12} justifyContent={"space-between"}>
          <Grid item xs={12}>
            <Controller
              name="copyEnabled"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                return (
                  <SingleSelect
                    id="select_copyEnabled"
                    disabled={loading || !pdfFileCount}
                    optionList={docControlOptions}
                    label=""
                    fullWidth={true}
                    idField="id"
                    labelField="label"
                    handleOptionSelection={(event: SelectChangeEvent) =>
                      onChange(event.target.value)
                    }
                    defaultValue={"keepExisting"}
                    value={value ?? ""}
                    error={!!error}
                    noGutter={true}
                    helperText={error?.message ?? ""}
                  />
                );
              }}
              control={control}
            />
          </Grid>
        </Grid>
      </Grid>
      <HorizontalBox
        addTopShadow
        attachToBottom={true}
        hidden={false}
        fullWidth
      >
        <Button
          id="btn_edit_file_detail_panel_cancel"
          variant="outlined"
          onClick={onClose}
          text={"Cancel"}
          color={"secondary"}
          fullWidth
          addSpaceBetweenButtons
        />

        <Button
          id={"btn_investor_detail_panel_save"}
          variant="contained"
          onClick={handleSubmit(handleSave)}
          text={"Save & Close"}
          color={"primary"}
          fullWidth
          addSpaceBetweenButtons
          disabled={loading || !pdfFileCount}
        />
      </HorizontalBox>
    </React.Fragment>
  );
};

export default EditPanel;
